import { ArrowRight, Laptop, Smartphone } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import { Link } from "react-router-dom";

// One customer story for every Halo, Fortress and SecureShield page.
// Mac: Fortress leads ("Fortress — the Mac app…"); standalones for one layer.
// iPhone: Halo SuperVPN under the same subscription. Fortress is Mac only.
export const DEVICE_STORY_LINE = "Fortress — the Mac app that also watches the Mac. Halo SuperVPN on every device.";

const IPHONE_HALO = "Install Halo SuperVPN on your iPhone. It uses the same subscription.";

const stories = {
  halo: {
    mac: "Fortress — the Mac app with Halo SuperVPN and SecureShield together, at the same Halo price. Want one app? Choose Fortress. Want one layer? Use Halo SuperVPN on its own.",
    iphone: IPHONE_HALO,
    link: { to: "/halo-fortress", label: "Discover Fortress" },
  },
  fortress: {
    mac: "Fortress — the Mac app with Halo SuperVPN and SecureShield together, at the same Halo price. Want one app? That’s Fortress. Want one layer? Choose Halo SuperVPN or SecureShield on its own.",
    iphone: IPHONE_HALO,
    link: { to: "/halo", label: "Discover Halo SuperVPN" },
  },
  "fortress-pro": {
    mac: "Fortress Pro — the Mac app with Halo SuperVPN and SecureShield Pro together: the Halo price plus US$11.99 once. Want one app? That’s Fortress Pro. Want one layer? Choose Halo SuperVPN or SecureShield Pro on its own.",
    iphone: IPHONE_HALO,
    link: { to: "/halo", label: "Discover Halo SuperVPN" },
  },
  securemac: {
    mac: "Fortress — the Mac app with Halo SuperVPN and SecureShield together. Want one app? Choose Fortress. Want one layer? SecureShield checks your Mac for US$4.99 once.",
    iphone: "SecureShield is made for Mac. On your iPhone, install Halo SuperVPN for a private connection.",
    link: { to: "/halo-fortress", label: "Discover Fortress" },
  },
  "securemac-pro": {
    mac: "Fortress Pro — the Mac app with Halo SuperVPN and SecureShield Pro together. Want one app? Choose Fortress Pro. Want one layer? SecureShield Pro is SecureShield plus Network Guard, US$19.99 once.",
    iphone: "SecureShield Pro is made for Mac. On your iPhone, install Halo SuperVPN for a private connection.",
    link: { to: "/halo-fortress-pro", label: "Discover Fortress Pro" },
  },
};

export default function DeviceStory({ product, showLine = true }) {
  const story = stories[product] || stories.halo;
  const cards = [
    ["mac", "On your Mac", story.mac, Laptop],
    ["iphone", "On your iPhone", story.iphone, Smartphone],
  ];
  return (
    <section className="lf-editorial-section border-b border-ink-100" data-testid="device-story" aria-labelledby={`device-story-${product}`}>
      <div className="lf-container">
        <div className="max-w-3xl">
          <p className="lf-eyebrow">Mac and iPhone</p>
          <h2 id={`device-story-${product}`} className="mt-4 font-display text-4xl font-black leading-[1.02] md:text-5xl">The right app for each device.</h2>
        </div>
        <div className="mt-10 grid gap-px border border-ink-100 bg-ink-100 md:grid-cols-2">
          {cards.map(([key, title, body, Icon]) => (
            <SectionReveal as="article" key={key} className="lf-feature-panel bg-white p-7 md:p-9" data-testid={`device-story-${key}`}>
              <Icon className="h-6 w-6 text-electric" strokeWidth={1.5} aria-hidden="true" />
              <h3 className="mt-5 font-display text-2xl font-bold">{title}</h3>
              <p className="mt-3 leading-relaxed text-ink-600">{body}</p>
            </SectionReveal>
          ))}
        </div>
        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {showLine ? <p className="max-w-2xl font-semibold text-ink-900" data-testid="device-story-line">{DEVICE_STORY_LINE}</p> : <span aria-hidden="true" />}
          <Link to={story.link.to} className="lf-link inline-flex min-h-11 shrink-0 items-center gap-2">{story.link.label} <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
        </div>
      </div>
    </section>
  );
}
